<template>
  <div class="pc-news-layout" :class="`is-${variant}`">
    <NewsCarousel
      class="featured-news"
      v-model="currentIndex"
      :items="items"
      @click="emitClick"
    />

    <div class="focus-list">
      <article
        v-for="(item, index) in items"
        :key="index"
        class="focus-card"
        :class="{ active: index === currentIndex }"
        @mouseenter="setActive(index)"
        @click="handleCardClick(index)"
      >
        <h3>{{ getNewsTitle(item) }}</h3>
        <p>{{ getNewsDesc(item) }}</p>
        <time>{{ getNewsDate(item) }}</time>
      </article>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import NewsCarousel from './NewsCarousel.vue';

export default Vue.extend({
  name: 'NewsLayout',
  components: {
    NewsCarousel,
  },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    value: {
      type: Number,
      default: 0,
    },
    variant: {
      type: String,
      default: 'home',
    },
  },
  data() {
    return {
      currentIndex: this.value,
    };
  },
  watch: {
    value(index) {
      if (index !== this.currentIndex) {
        this.currentIndex = index;
      }
    },
    currentIndex(index) {
      this.$emit('input', index);
      this.$emit('change', index);
    },
  },
  methods: {
    getNewsData(item) {
      return item.data || item;
    },
    getNewsTitle(item) {
      const data = this.getNewsData(item);

      return data.customTitle || data.title || '';
    },
    getNewsDesc(item) {
      const data = this.getNewsData(item);

      return this.stripHtml(data.desc || data.extText1 || '');
    },
    getNewsDate(item) {
      const data = this.getNewsData(item);

      if (data.date) {
        return data.date;
      }
      if (data.publishTime) {
        return data.publishTime;
      }

      return this.formatPublishDate(data.publishDate);
    },
    stripHtml(value) {
      return String(value || '').replace(/<[^>]*>/g, '');
    },
    formatPublishDate(value) {
      if (!value) {
        return '';
      }

      const time = Number(value);
      if (!Number.isNaN(time)) {
        const date = new Date(String(value).length === 10 ? time * 1000 : time);

        if (!Number.isNaN(date.getTime())) {
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');

          return `${month}-${day}`;
        }
      }

      return String(value);
    },
    setActive(index) {
      this.currentIndex = index;
    },
    handleCardClick(index) {
      this.setActive(index);
      this.goNewsDetail();
    },
    getNewsPath(item) {
      const data = this.getNewsData(item);

      return data.newsPath || '';
    },
    buildNewsUrl(newsPath) {
      const path = String(newsPath || '');

      if (!path) {
        return '';
      }

      const normalizedPath = path.replace(/^\/?\d+/, '');

      return `https://www.cqcb.com${
        normalizedPath.charAt(0) === '/' ? normalizedPath : `/${normalizedPath}`
      }`;
    },
    goNewsDetail() {
      const newsPath = this.getNewsPath(this.items[this.currentIndex]);
      const url = this.buildNewsUrl(newsPath);

      if (!url) {
        this.$emit('click', this.currentIndex);
        return;
      }
      window.open(url, '_blank');
    },
    emitClick() {
      this.goNewsDetail();
    },
  },
});
</script>

<style lang="less" scoped>
.pc-news-layout {
  display: grid;
}

.featured-news {
  width: 100%;
}

.focus-list {
  display: flex;
  flex-direction: column;
}

.focus-card {
  overflow: hidden;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 5px 16px rgba(28, 54, 80, 0.1);
  box-sizing: border-box;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;

  &:hover {
    box-shadow: 0 12px 26px rgba(28, 54, 80, 0.14);
    transform: translateY(-2px);
  }

  h3 {
    margin: 0;
    overflow: hidden;
    color: #25313d;
    font-weight: 700;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  p {
    display: -webkit-box;
    margin: 0;
    overflow: hidden;
    color: #687480;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
  }

  time {
    display: block;
    color: #7a858f;
  }

  &.active {
    background: #2088dc;

    h3,
    p,
    time {
      color: #fff;
    }
  }
}

.is-home {
  grid-template-columns: minmax(0, 740px) minmax(0, 1fr);
  gap: 34px;

  .featured-news {
    height: 430px;
  }

  .focus-list {
    gap: 18px;
  }

  .focus-card {
    height: 131px;
    padding: 19px 24px 17px;

    h3 {
      margin-bottom: 8px;
      font-size: 20px;
      line-height: 28px;
    }

    p {
      margin-bottom: 8px;
      font-size: 15px;
      line-height: 22px;
    }

    time {
      font-size: 15px;
      line-height: 20px;
    }
  }
}

.is-news {
  grid-template-columns: 586px minmax(0, 1fr);
  gap: 28px;

  .featured-news {
    height: 342px;
  }

  .focus-list {
    gap: 14px;
  }

  .focus-card {
    height: 104px;
    padding: 14px 18px 12px;

    h3 {
      margin-bottom: 6px;
      font-size: 16px;
      line-height: 22px;
    }

    p {
      margin-bottom: 5px;
      font-size: 13px;
      line-height: 19px;
    }

    time {
      font-size: 13px;
      line-height: 17px;
    }
  }
}

@media (max-width: 1280px) {
  .is-home {
    grid-template-columns: minmax(0, 640px) minmax(0, 1fr);
    gap: 28px;

    .focus-card {
      padding-right: 18px;
      padding-left: 18px;

      h3 {
        font-size: 18px;
      }
    }
  }
}
</style>
